import {
  buyerCurrencyFormatter,
  numberWithSeparator,
} from '@diamond/shared/utils';

const STOCK_MAGNIFIER = 1000;

type SingleDiscountPromo = {
  deduction_amount?: number | null;
  is_discount?: boolean | null;
  mechanism?: string | null;
  minimum_order_amount?: number | null;
  minimum_order_quantity?: number | null;
  price_after_discount?: number | null;
};

type SingleFreeGoodPromo = {
  mechanism?: string | null;
  minimum_order_amount?: number | null;
  minimum_order_quantity?: number | null;
  quantity_free_goods?: number | null;
  sales_unit_desc?: string | null;
};

type BundledPromo = {
  mechanism?: string | null;
  sum_minimum_order_amount?: number | null;
  sum_minimum_order_quantity?: number | null;
};

type BundledDiscountPromo = BundledPromo & {
  is_discount?: boolean | null;
  deduction_amount?: number | null;
};

type BundledFreeGoodPromo = BundledPromo & {
  quantity_free_goods?: number | null;
  sales_unit?: string | null;
  sales_unit_desc?: string | null;
};

export type PromoPreviewProduct = {
  minimum_order_quantity?: number | null;
  sales_unit?: string | null;
  sales_unit_desc?: string | null;
  prices?: Array<{
    price: number;
  }>;
  discount_promos?: SingleDiscountPromo[];
  free_good_promos?: SingleFreeGoodPromo[];
  bundled_discount_promos?: BundledDiscountPromo[];
  bundled_free_good_promos?: BundledFreeGoodPromo[];
};

type ProductPromoPreview = {
  cardText: string;
  modalText: string;
  ctaLabel?: string;
  targetQuantity?: number;
};

export type ProductPromoOffer = {
  type: 'discount' | 'free-item' | 'bundle';
  badgeLabel: string;
  text: string;
  targetPriceLabel?: string;
  targetQuantity?: number;
};

function formatQuantity(quantity: number): string {
  const normalized = quantity / STOCK_MAGNIFIER;
  const value = Number.isInteger(normalized)
    ? String(normalized)
    : normalized.toFixed(2);

  return numberWithSeparator(value);
}

function formatUnit(unit?: string | null): string {
  return unit?.toLowerCase() || 'unit';
}

function formatThreshold(
  amount?: number | null,
  quantity?: number | null,
  unit?: string | null
): string | undefined {
  if (amount && amount > 0) {
    return buyerCurrencyFormatter(amount);
  }

  if (quantity && quantity > 0) {
    return `${formatQuantity(quantity)} ${formatUnit(unit)}`;
  }

  return undefined;
}

function formatDiscountValue(promo: { deduction_amount?: number | null; is_discount?: boolean | null }): string | undefined {
  if (!promo.deduction_amount || promo.deduction_amount <= 0) {
    return undefined;
  }

  if (promo.is_discount) {
    const percentage = promo.deduction_amount / 100;
    return `${
      Number.isInteger(percentage) ? percentage : percentage.toFixed(1)
    }%`;
  }

  return buyerCurrencyFormatter(promo.deduction_amount);
}

function formatShortCurrency(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return '';
  }

  if (value >= 1000000) {
    const millions = value / 1000000;
    const formatted = Number.isInteger(millions)
      ? String(millions)
      : millions
          .toFixed(2)
          .replace(/\.?0+$/, '')
          .replace('.', ',');

    return `Rp${formatted}jt`;
  }

  if (value >= 1000) {
    return `Rp${Math.round(value / 1000)}rb`;
  }

  return buyerCurrencyFormatter(value);
}

function getTargetTotalLabel(
  product: PromoPreviewProduct,
  targetQuantity?: number | null,
  priceAfterDiscount?: number | null
): string | undefined {
  if (!targetQuantity || targetQuantity <= 0) {
    return undefined;
  }

  const unitPrice = priceAfterDiscount || product.prices?.[0]?.price;
  if (!unitPrice) {
    return undefined;
  }

  return formatShortCurrency((targetQuantity / STOCK_MAGNIFIER) * unitPrice);
}

function buildSingleDiscountPreview(
  promo: SingleDiscountPromo,
  unit: string,
  currentQuantity?: number
): ProductPromoPreview | undefined {
  const discountValue = formatDiscountValue(promo);
  const threshold = formatThreshold(
    promo.minimum_order_amount,
    promo.minimum_order_quantity,
    unit
  );

  if (!discountValue || !threshold) {
    return undefined;
  }

  const promoType = promo.is_discount ? 'Diskon' : 'Cashback';
  const minQuantity = promo.minimum_order_quantity || 0;
  const remainingQuantity = Math.max(0, minQuantity - (currentQuantity || 0));
  const usesMinimumAmount = (promo.minimum_order_amount ?? 0) > 0;
  const canUsePromoQuantity =
    minQuantity > 0 && !usesMinimumAmount && remainingQuantity > 0;

  return {
    cardText: `${promoType} ${discountValue} min. ${threshold}`,
    modalText: usesMinimumAmount
      ? `${promoType} ${discountValue} tersedia untuk minimum pembelian ${threshold}`
      : canUsePromoQuantity
      ? `Tambah ${formatQuantity(remainingQuantity)} ${formatUnit(
          unit
        )} lagi untuk aktifkan ${promoType.toLowerCase()} ${discountValue}`
      : `${promoType} ${discountValue} aktif untuk pesanan ini`,
    ctaLabel: canUsePromoQuantity ? 'Pakai qty promo' : undefined,
    targetQuantity: canUsePromoQuantity ? minQuantity : undefined,
  };
}

function buildSingleFreeGoodPreview(
  promo: SingleFreeGoodPromo,
  unit: string,
  currentQuantity?: number
): ProductPromoPreview | undefined {
  const threshold = formatThreshold(
    promo.minimum_order_amount,
    promo.minimum_order_quantity,
    unit
  );

  if (!threshold) {
    return undefined;
  }

  const freeGoodQuantity = promo.quantity_free_goods
    ? `${formatQuantity(promo.quantity_free_goods)} ${formatUnit(
        promo.sales_unit_desc || unit
      )}`
    : undefined;
  const minQuantity = promo.minimum_order_quantity || 0;
  const remainingQuantity = Math.max(0, minQuantity - (currentQuantity || 0));
  const usesMinimumAmount = (promo.minimum_order_amount ?? 0) > 0;
  const canUsePromoQuantity =
    minQuantity > 0 && !usesMinimumAmount && remainingQuantity > 0;
  const rewardText = freeGoodQuantity
    ? `gratis ${freeGoodQuantity}`
    : 'bonus produk';

  return {
    cardText: `Promo ${rewardText} min. ${threshold}`,
    modalText: usesMinimumAmount
      ? `Promo ${rewardText} tersedia untuk minimum pembelian ${threshold}`
      : canUsePromoQuantity
      ? `Tambah ${formatQuantity(remainingQuantity)} ${formatUnit(
          unit
        )} lagi untuk dapat ${rewardText}`
      : `Promo ${rewardText} aktif untuk pesanan ini`,
    ctaLabel: canUsePromoQuantity ? 'Pakai qty promo' : undefined,
    targetQuantity: canUsePromoQuantity ? minQuantity : undefined,
  };
}

function buildBundledPreview(
  promo: BundledPromo,
  unit: string
): ProductPromoPreview {
  const threshold = formatThreshold(
    promo.sum_minimum_order_amount,
    promo.sum_minimum_order_quantity,
    unit
  );
  const mechanism = promo.mechanism || 'cek syarat kombinasi produk';
  const suffix = threshold ? ` min. total ${threshold}` : '';

  return {
    cardText: `Promo bundle tersedia${suffix}`,
    modalText: `Promo bundle tersedia: ${mechanism}`,
  };
}

export function buildProductPromoPreview(
  product?: PromoPreviewProduct,
  unitLabel?: string,
  currentQuantity?: number
): ProductPromoPreview | undefined {
  if (!product) return undefined;

  const unit =
    unitLabel || product.sales_unit || product.sales_unit_desc || 'unit';
  const singleDiscount = product.discount_promos?.[0];
  const singleFreeGood = product.free_good_promos?.[0];
  const bundledDiscount = product.bundled_discount_promos?.[0];
  const bundledFreeGood = product.bundled_free_good_promos?.[0];

  if (singleDiscount) {
    const preview = buildSingleDiscountPreview(
      singleDiscount,
      unit,
      currentQuantity
    );
    if (preview) return preview;
  }

  if (singleFreeGood) {
    const preview = buildSingleFreeGoodPreview(
      singleFreeGood,
      unit,
      currentQuantity
    );
    if (preview) return preview;
  }

  if (bundledDiscount) {
    return buildBundledPreview(bundledDiscount, unit);
  }

  if (bundledFreeGood) {
    return buildBundledPreview(bundledFreeGood, unit);
  }

  return undefined;
}

export function buildProductPromoOffers(
  product?: PromoPreviewProduct
): ProductPromoOffer[] {
  if (!product) return [];

  const discountOffers =
    product.discount_promos
      ?.map((promo): ProductPromoOffer | undefined => {
        const discountValue = formatDiscountValue(promo);
        const targetQuantity = promo.minimum_order_quantity || undefined;

        if (!discountValue || !targetQuantity || promo.minimum_order_amount) {
          return undefined;
        }

        return {
          type: 'discount',
          badgeLabel: promo.is_discount ? 'Diskon' : 'Cashback',
          text: `Beli ${formatQuantity(targetQuantity)} Diskon ${discountValue}`,
          targetPriceLabel: getTargetTotalLabel(
            product,
            targetQuantity,
            promo.price_after_discount
          ),
          targetQuantity,
        };
      })
      .filter((offer): offer is ProductPromoOffer => offer !== undefined) || [];

  const freeGoodOffers =
    product.free_good_promos
      ?.map((promo): ProductPromoOffer | undefined => {
        const targetQuantity = promo.minimum_order_quantity || undefined;

        if (!targetQuantity || promo.minimum_order_amount) {
          return undefined;
        }

        const freeGoodQuantity = promo.quantity_free_goods
          ? formatQuantity(promo.quantity_free_goods)
          : undefined;
        const freeGoodText = freeGoodQuantity
          ? `Gratis ${freeGoodQuantity}`
          : 'Produk Gratis';

        return {
          type: 'free-item',
          badgeLabel: 'Produk Gratis',
          text: `Beli ${formatQuantity(targetQuantity)} ${freeGoodText}`,
          targetPriceLabel: getTargetTotalLabel(product, targetQuantity),
          targetQuantity,
        };
      })
      .filter((offer): offer is ProductPromoOffer => offer !== undefined) || [];

  const bundledDiscountOffers: ProductPromoOffer[] = (
    product.bundled_discount_promos || []
  ).map((promo) => {
    const qty = promo.sum_minimum_order_quantity;
    const discountValue = formatDiscountValue(promo);
    const discountText = discountValue ? `Diskon ${discountValue}` : 'Diskon Bundle';
    return {
      type: 'bundle',
      badgeLabel: 'Bundle',
      text: qty ? `Beli ${formatQuantity(qty)} ${discountText}` : `Promo ${discountText}`,
      targetQuantity: qty || undefined,
    };
  });

  const bundledFreeGoodOffers: ProductPromoOffer[] = (
    product.bundled_free_good_promos || []
  ).map((promo) => {
    const qty = promo.sum_minimum_order_quantity;
    const freeQty = promo.quantity_free_goods
      ? formatQuantity(promo.quantity_free_goods)
      : undefined;
    const unit = formatUnit(promo.sales_unit_desc || promo.sales_unit || product.sales_unit_desc || product.sales_unit);
    const freeText = freeQty ? `Gratis ${freeQty} ${unit}` : 'Gratis Bundle';
    // bundled_free_good_promos are tied to a specific product_id (product-level threshold),
    // so they behave the same as free_good_promos for UI purposes — use 'free-item' type
    // to allow isActive/canUse state based on this product's quantity.
    return {
      type: 'free-item',
      badgeLabel: 'Produk Gratis',
      text: qty ? `Beli ${formatQuantity(qty)} ${freeText}` : freeText,
      targetQuantity: qty || undefined,
    };
  });

  const bundledOffers = [...bundledDiscountOffers, ...bundledFreeGoodOffers];

  return [...discountOffers, ...freeGoodOffers, ...bundledOffers];
}
